import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

type Language = 'en' | 'tr'; // English and Turkish for now, can be expanded

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Use localStorage to persist language preference
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage?.toLowerCase() as Language) || 'en';
  });

  // Update language in localStorage and trigger updates
  useEffect(() => {
    localStorage.setItem('language', language);
    
    // Apply the language to the document element
    document.documentElement.setAttribute('data-language', language);
    document.documentElement.setAttribute('lang', language);

    // Dispatch a custom event to notify all components
    const event = new CustomEvent('languageChanged', { 
      detail: { language },
      bubbles: true,
      composed: true
    });
    window.dispatchEvent(event);

    // Force a re-render of all components using useTranslation
    const forceUpdateEvent = new CustomEvent('forceLanguageUpdate', {
      detail: { timestamp: Date.now() },
      bubbles: true,
      composed: true
    });
    window.dispatchEvent(forceUpdateEvent);
  }, [language]);

  // Listen for language changes in other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'language' && e.newValue) {
        setLanguage(e.newValue.toLowerCase() as Language);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}; 